import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Tag, Plus, Trash2, X, Eye, EyeOff, Search, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";

const API = window.location.origin;

const PRESET_COLORS = [
  "#2563EB", "#6B7280", "#7C3AED", "#EA580C", "#16A34A",
  "#DC2626", "#0891B2", "#DB2777", "#0E7490", "#B45309",
  "#4F46E5", "#059669", "#D97706", "#9333EA", "#E11D48",
];

const MATCH_FIELDS = [
  { value: "option_name", label: "Opsiyon Adi" },
  { value: "product_name", label: "Urun Adi" },
  { value: "both", label: "Ikisi de (Urun + Opsiyon)" },
];

const AUTO_BADGES = [
  { label: "Transferli", pattern: "transferli / with transfer / hotel transfer", color: "#2563EB" },
  { label: "Kendi Gelir", pattern: "kendi gelir / no transfer / meeting point", color: "#6B7280" },
  { label: "VIP", pattern: "vip", color: "#7C3AED" },
  { label: "VIP Lounge", pattern: "vip lounge / vip loca", color: "#4C1D95" },
  { label: "Alkollu", pattern: "alkollu / alcoholic / alcohole", color: "#EA580C" },
  { label: "Alkolsuz", pattern: "alkolsuz / soft drink / non-alcohol", color: "#16A34A" },
  { label: "Limitsiz", pattern: "limitsiz / unlimited", color: "#DC2626" },
  { label: "Yemeksiz", pattern: "yemeksiz / no meal", color: "#57534E" },
  { label: "Yemekli", pattern: "yemekli / with meal", color: "#65A30D" },
  { label: "Snack", pattern: "snack", color: "#0891B2" },
  { label: "PVT", pattern: "private table / ozel masa", color: "#0E7490" },
  { label: "Romantic", pattern: "romantic", color: "#DB2777" },
  { label: "Cam Kenari", pattern: "window / cam kenar", color: "#B45309" },
  { label: "Kabatas", pattern: "kabataş / kabatas (kalkis noktasi)", color: "#1E40AF" },
  { label: "Cibali", pattern: "cibali (kalkis noktasi)", color: "#4338CA" },
];

export default function TagManagement() {
  const [customTags, setCustomTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editTag, setEditTag] = useState(null);
  const [formField, setFormField] = useState("both");
  const [formPattern, setFormPattern] = useState("");
  const [formLabel, setFormLabel] = useState("");
  const [formColor, setFormColor] = useState("#6B7280");
  const [saving, setSaving] = useState(false);
  // Kapsam / Kesif raporu
  const [coverage, setCoverage] = useState(null);
  const [covLoading, setCovLoading] = useState(false);
  const [covOnlyUnrec, setCovOnlyUnrec] = useState(false);
  const [covSearch, setCovSearch] = useState("");

  const token = localStorage.getItem("auth_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTags = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/custom-tags`, { headers });
      setCustomTags(data.tags || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTags(); }, [fetchTags]);

  const resetForm = () => {
    setEditTag(null);
    setFormField("both");
    setFormPattern("");
    setFormLabel("");
    setFormColor("#6B7280");
    setShowForm(false);
  };

  const openEdit = (tag) => {
    setEditTag(tag);
    setFormField(tag.match_field);
    setFormPattern(tag.match_pattern);
    setFormLabel(tag.tag_label);
    setFormColor(tag.tag_color);
    setShowForm(true);
  };

  const saveTag = async () => {
    if (!formPattern || !formLabel) return;
    setSaving(true);
    try {
      const payload = { match_field: formField, match_pattern: formPattern, tag_label: formLabel, tag_color: formColor };
      if (editTag) {
        await axios.put(`${API}/api/custom-tags/${editTag.id}`, payload, { headers });
      } else {
        await axios.post(`${API}/api/custom-tags`, payload, { headers });
      }
      resetForm();
      fetchTags();
    } catch (e) { alert("Kaydedilemedi"); }
    setSaving(false);
  };

  const deleteTag = async (id) => {
    if (!window.confirm("Bu etiketi silmek istediginize emin misiniz?")) return;
    try {
      await axios.delete(`${API}/api/custom-tags/${id}`, { headers });
      fetchTags();
    } catch (e) { console.error(e); }
  };

  const toggleTag = async (id) => {
    try {
      await axios.put(`${API}/api/custom-tags/${id}/toggle`, {}, { headers });
      fetchTags();
    } catch (e) { console.error(e); }
  };

  const loadCoverage = async () => {
    setCovLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/addon-coverage`, { headers });
      setCoverage(data);
    } catch (e) { alert("Kapsam raporu yuklenemedi"); }
    setCovLoading(false);
  };

  const filteredCombos = (coverage?.combos || []).filter(c => {
    if (covOnlyUnrec && c.recognized) return false;
    if (covSearch) {
      const q = covSearch.toLowerCase();
      if (!((c.product_name || "").toLowerCase().includes(q) || (c.option_name || "").toLowerCase().includes(q))) return false;
    }
    return true;
  });

  if (loading) return <div className="p-8 text-center text-slate-400">Yukleniyor...</div>;

  return (
    <div className="min-h-screen" style={{ maxWidth: "100vw", overflowX: "hidden" }}>
      <div className="page-header">
        <div className="flex items-center gap-3">
          <Tag className="w-5 h-5 text-blue-400" />
          <div>
            <h1 className="text-lg font-bold text-white">Etiket Yonetimi</h1>
            <p className="text-xs text-slate-500">Opsiyonlarda hangi etiketlerin gorunecegini duzenleyin</p>
          </div>
        </div>
      </div>

      <div className="page-body space-y-6">
        {/* Auto Badges Info */}
        <div className="glass-card p-5">
          <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-3">Otomatik Etiketler (Sistem)</h2>
          <p className="text-xs text-slate-500 mb-3">Bu etiketler opsiyon/urun adindaki kelimelere gore otomatik algilanir. Degistirilemez ama ozel etiketlerle genisletilebilir.</p>
          <div className="flex flex-wrap gap-2">
            {AUTO_BADGES.map(b => (
              <div key={b.label} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800/30 border border-slate-700">
                <span className="text-[11px] font-bold px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: b.color }}>{b.label}</span>
                <span className="text-[10px] text-slate-400">{b.pattern}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Addon Kapsam / Kesif Raporu */}
        <div className="glass-card p-5" data-testid="addon-coverage-card">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div>
              <h2 className="text-sm font-bold text-white uppercase tracking-wider">Addon Kapsam Raporu</h2>
              <p className="text-xs text-slate-500 mt-0.5">Bókun verisindeki tum urun/opsiyon kombinasyonlarini tarar; hangi rozetleri urettigini gosterir ve <span className="text-amber-400 font-semibold">taninmayanlari</span> vurgular. Boylece addon kacmaz.</p>
            </div>
            <button onClick={loadCoverage} disabled={covLoading} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors" data-testid="coverage-scan-btn">
              {covLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
              {covLoading ? "Taraniyor..." : coverage ? "Yeniden Tara" : "Tumunu Tara"}
            </button>
          </div>

          {coverage && (
            <>
              <div className="flex flex-wrap gap-3 my-3">
                <div className="px-3 py-2 rounded-lg bg-slate-800/40 border border-slate-700">
                  <div className="text-[10px] text-slate-500 uppercase">Rezervasyon</div>
                  <div className="text-lg font-bold text-white">{coverage.total_bookings?.toLocaleString("tr-TR")}</div>
                </div>
                <div className="px-3 py-2 rounded-lg bg-slate-800/40 border border-slate-700">
                  <div className="text-[10px] text-slate-500 uppercase">Farkli Kombinasyon</div>
                  <div className="text-lg font-bold text-white">{coverage.distinct_combos}</div>
                </div>
                <div className={`px-3 py-2 rounded-lg border ${coverage.unrecognized_combos > 0 ? "bg-amber-500/10 border-amber-500/40" : "bg-green-500/10 border-green-500/40"}`}>
                  <div className="text-[10px] text-slate-500 uppercase">Taninmayan</div>
                  <div className={`text-lg font-bold ${coverage.unrecognized_combos > 0 ? "text-amber-400" : "text-green-400"}`}>
                    {coverage.unrecognized_combos} kombo / {coverage.unrecognized_bookings?.toLocaleString("tr-TR")} rez
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <div className="relative flex-1 min-w-[180px]">
                  <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <input value={covSearch} onChange={e => setCovSearch(e.target.value)} placeholder="Urun/opsiyon ara..." className="w-full bg-slate-800 border border-slate-700 text-white text-xs rounded-lg pl-8 pr-3 py-1.5 placeholder:text-slate-500" data-testid="coverage-search" />
                </div>
                <label className="flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer select-none">
                  <input type="checkbox" checked={covOnlyUnrec} onChange={e => setCovOnlyUnrec(e.target.checked)} className="accent-amber-500" data-testid="coverage-only-unrec" />
                  Sadece taninmayanlar
                </label>
              </div>

              <div className="max-h-[380px] overflow-y-auto rounded-lg border border-slate-700" data-testid="coverage-table">
                <table className="w-full text-left">
                  <thead className="sticky top-0 bg-slate-800 text-[10px] uppercase text-slate-400">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Urun / Opsiyon</th>
                      <th className="px-3 py-2 font-semibold text-right w-16">Adet</th>
                      <th className="px-3 py-2 font-semibold w-1/3">Rozetler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCombos.map((c, i) => (
                      <tr key={i} className={`border-t border-slate-800 ${c.recognized ? "" : "bg-amber-500/5"}`} data-testid={`coverage-row-${i}`}>
                        <td className="px-3 py-2 align-top">
                          <div className="text-xs text-white font-medium break-words">{c.product_name || <span className="text-slate-600 italic">(urun yok)</span>}</div>
                          {c.option_name && c.option_name !== "Standard rate" && (
                            <div className="text-[10px] text-slate-400 break-words">{c.option_name}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 align-top text-right text-xs text-slate-300 tabular-nums">{c.count.toLocaleString("tr-TR")}</td>
                        <td className="px-3 py-2 align-top">
                          {c.recognized ? (
                            <div className="flex flex-wrap gap-1">
                              {c.badges.map((b, bi) => (
                                <span key={bi} className="text-[10px] font-bold px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: b.bg }}>{b.label}</span>
                              ))}
                            </div>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-400"><AlertTriangle className="w-3 h-3" /> Rozet yok — kontrol et</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {filteredCombos.length === 0 && (
                      <tr><td colSpan={3} className="px-3 py-6 text-center text-slate-500 text-xs">Sonuc yok</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-slate-500 mt-2 flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-400" /> Taninmayan bir satir gorurseniz asagidan "Yeni Etiket" ekleyerek o kelimeyi rozete donusturebilirsiniz.</p>
            </>
          )}
        </div>

        {/* Custom Tags */}
        <div className="glass-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-white uppercase tracking-wider">Ozel Etiketler</h2>
            <button onClick={() => { resetForm(); setShowForm(true); }} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-lg transition-colors" data-testid="add-tag-btn">
              <Plus className="w-3.5 h-3.5" /> Yeni Etiket
            </button>
          </div>

          {showForm && (
            <div className="bg-slate-800/50 rounded-lg p-4 mb-4 border border-blue-500/30" data-testid="tag-form">
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1 block">Eslesme Alani</label>
                    <select value={formField} onChange={e => setFormField(e.target.value)} className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2" data-testid="tag-field-select">
                      {MATCH_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1 block">Etiket Adi (Kisaltma)</label>
                    <input value={formLabel} onChange={e => setFormLabel(e.target.value)} placeholder="orn: OZEL, CAM, MENU2" className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2 placeholder:text-slate-500" maxLength={8} data-testid="tag-label-input" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1 block">Aranacak Kelime/Kalip</label>
                  <input value={formPattern} onChange={e => setFormPattern(e.target.value)} placeholder="orn: cam kenari, window seat, premium" className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2 placeholder:text-slate-500" data-testid="tag-pattern-input" />
                  <p className="text-[10px] text-slate-500 mt-1">Opsiyon veya urun adinda bu kelime gecerse etiket otomatik eklenir (kucuk/buyuk harf farketmez)</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1 block">Renk</label>
                  <div className="flex flex-wrap gap-1.5">
                    {PRESET_COLORS.map(c => (
                      <button key={c} onClick={() => setFormColor(c)} className="w-7 h-7 rounded-lg border-2 transition-all" style={{ backgroundColor: c, borderColor: formColor === c ? "#fff" : "transparent" }} />
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-3 pt-1">
                  <div className="text-xs text-slate-400">Onizleme:</div>
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded text-white" style={{ backgroundColor: formColor }}>{formLabel || "..."}</span>
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={saveTag} disabled={saving || !formPattern || !formLabel} className="px-4 py-2 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white text-xs font-medium rounded-lg" data-testid="save-tag-btn">
                    {saving ? "Kaydediliyor..." : editTag ? "Guncelle" : "Kaydet"}
                  </button>
                  <button onClick={resetForm} className="px-4 py-2 bg-slate-700 text-slate-300 text-xs rounded-lg">Iptal</button>
                </div>
              </div>
            </div>
          )}

          {customTags.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">Henuz ozel etiket eklenmemis. Otomatik etiketler calismaya devam ediyor.</div>
          ) : (
            <div className="space-y-2">
              {customTags.map(tag => (
                <div key={tag.id} className={`flex items-center gap-3 p-3 rounded-lg border ${tag.active ? "bg-slate-800/30 border-slate-700" : "bg-slate-900/50 border-slate-800 opacity-50"}`} data-testid={`tag-row-${tag.id}`}>
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded text-white shrink-0" style={{ backgroundColor: tag.tag_color }}>{tag.tag_label}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white font-medium">"{tag.match_pattern}"</div>
                    <div className="text-[10px] text-slate-500">
                      {tag.match_field === "both" ? "Urun + Opsiyon" : tag.match_field === "option_name" ? "Opsiyon Adi" : "Urun Adi"} icinde aranir
                    </div>
                  </div>
                  <button onClick={() => toggleTag(tag.id)} className={`p-1.5 rounded hover:bg-slate-700 transition-colors ${tag.active ? "text-green-400" : "text-slate-500"}`} title={tag.active ? "Devre disi birak" : "Aktif et"}>
                    {tag.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button onClick={() => openEdit(tag)} className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-white text-xs">Duzenle</button>
                  <button onClick={() => deleteTag(tag.id)} className="p-1.5 rounded hover:bg-red-500/20 text-slate-500 hover:text-red-400" data-testid={`delete-tag-${tag.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
